import { useMemo } from 'react';

import { Box, Heading } from '@chakra-ui/react';

import { Glyph } from '~/components/glyph';
import { Link } from '~/components/link';

import { LegalDocumentMetadata, LegalSection as LegalSectionType } from '../models/legal-documents-metadata';
import { getMetadataBySubSection } from '../utils/get-metadata-by-subsection';
import { getSubSectionTitle } from '../utils/get-subsection-title';

import { LegalSectionCard } from './legal-section-card';
import { LegalSectionGrid } from './legal-section-grid';

export function LegalSection({
  title,
  glyphName,
  legalDocumentsMetadata,
  section,
}: {
  title: string;
  glyphName: string;
  legalDocumentsMetadata: LegalDocumentMetadata[];
  section: LegalSectionType;
}) {
  const subSectionsMetadata = useMemo(() => {
    // All subsections of a section must be defined, if any in the section are.
    if (legalDocumentsMetadata.some((metadata) => metadata.subSection === null)) {
      return [];
    }

    const allSubSectionsMap = new Set(legalDocumentsMetadata.map((metadata) => metadata.subSection!));
    const allSubSections = [...allSubSectionsMap];

    return allSubSections;
  }, [legalDocumentsMetadata]);

  const hasSubSection = Boolean(subSectionsMetadata.length);

  return (
    <Box as='section' id={section}>
      <Box alignItems='center' display='flex' marginBottom='8px'>
        <Glyph height={48} name={glyphName} width={48} />
        <Heading as='h2' size='h3'>
          {title}
        </Heading>
      </Box>

      {hasSubSection &&
        subSectionsMetadata.map((subSection) => {
          const metadata = getMetadataBySubSection(legalDocumentsMetadata, subSection).sort(
            ({ linkDisplayOrder: a }, { linkDisplayOrder: b }) => a - b
          );

          return (
            <LegalSectionCard key={subSection} display='flex' flexDirection='column' marginBottom='24px'>
              <Heading as='h3' marginBottom='24px' size='h4'>
                {getSubSectionTitle(subSection)}
              </Heading>

              <LegalSectionGrid>
                {metadata.map((metadata) => (
                  <Link key={metadata._id} _visited={{ color: '' }} href={`/legal/${metadata.slug}`}>
                    {metadata.linkDisplayText}
                  </Link>
                ))}
              </LegalSectionGrid>
            </LegalSectionCard>
          );
        })}

      {!hasSubSection && (
        <LegalSectionCard display='flex' flexDirection='column'>
          <LegalSectionGrid>
            {legalDocumentsMetadata
              .sort(({ linkDisplayOrder: a }, { linkDisplayOrder: b }) => a - b)
              .map((legalDocumentMetadata) => (
                <Link
                  key={legalDocumentMetadata._id}
                  _visited={{ color: '' }}
                  href={`/legal/${legalDocumentMetadata.slug}`}
                >
                  {legalDocumentMetadata.linkDisplayText}
                </Link>
              ))}
          </LegalSectionGrid>
        </LegalSectionCard>
      )}
    </Box>
  );
}
